import { Box, Typography, Stack } from "@mui/material";
import BgImage from "../../../../assets/InsightsPageArtWork.svg";
import { DownloadCsvButton } from "./DownloadCsvButton";
import { makeStyles } from "@mui/styles";
import axios from "../../../../services/axios";
import { FileDownload } from "@backend-types";
import { ErrorToast } from "../../utils/Toast";
import React, { useContext, useEffect, useState } from "react";
import { DbUser, InsightsCtx, InsightsView } from "../../../../interfaces";
import { InsightsContext } from "../../../../context/InsightsContext";
import { HandleDataLayer } from "../../../../Helpers";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Button from "@mui/material/Button";
import { FontRoboto, v2theme } from "../../../../styles/Theme";

interface InsightsHeaderProps {
  showDownloadButton?: boolean;
  view: InsightsView;
  studentHeader?: string;
  classId: number;
}

const useStyle = makeStyles((theme) => ({
  headingContainer: {
    height: "400px",
    width: "100%",
    backgroundColor: "#0E556F",
    display: "flex",
    position: "relative",
    padding: "0px",
    alignItems: "center",
  },
  headingTextBox: {
    flexWrap: "nowrap",
    padding: "30px",
    flexGrow: 0, // Won't grow to fill extra space
    flexShrink: 1, // Will shrink when space is limited
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    maxWidth: "582px",
    minWidth: "300px",
  },
  bgImage: {
    flexGrow: 1,
    flexShrink: 0, // Won't shrink, thus will retain its dimensions
    backgroundPosition: "bottom right",
    backgroundRepeat: "no-repeat",
    backgroundSize: "contain",
    minWidth: "425px",
    height: "100%",
  },

  backButton: {
    fontFamily: FontRoboto,
    paddingLeft: "10px",
    paddingRight: "10px",
    backgroundColor: "transparent",
    width: "90px",
    borderRadius: "8px",
    color: "#FFFFFF",
    height: "45px",
    boxShadow: "none",
    fontSize: "16px",
    letterSpacing: "0.12px",
    fontWeight: v2theme.typography.fontWeightMedium,
    lineHeight: "27px",
    "&:hover": {
      backgroundColor: "#00000026",
      height: "45px",
      width: "90px",
      textDecoration: "underline",
      boxShadow: "none",
      transform: "none",
    },
  },
}));

export const InsightsHomeHeader = (props: InsightsHeaderProps) => {
  let { view = "class", studentHeader = "", classId } = props;
  const classes = useStyle();

  const showDownloadButton = false;

  const insightsCtx = useContext(InsightsContext) as InsightsCtx;
  const [teacherId, setTeacherId] = useState<string>("");
  useEffect(() => {
    if (view === "class" && insightsCtx.classInfo) {
      let currentClass = insightsCtx.classInfo;
      setTeacherId(currentClass?.teacher_id || "");
    } else if (view === "student" && insightsCtx.userInfo) {
      const currentStudent = insightsCtx.userInfo;
      const studentClass = currentStudent.class_student.find(
        (item) => item.class_id === classId
      )?.class;
      const studentClassTeacher = studentClass?.user as DbUser | undefined;
      setTeacherId(studentClassTeacher?.id || "");
    }
  }, [insightsCtx.classInfo, view, insightsCtx.userInfo, classId]);

  const handleStudentBack = () => {
    HandleDataLayer(
      "link",
      "click",
      "internal link",
      "main content",
      studentHeader,
      "Back",
      "onclick"
    );
    insightsCtx.handleGoBack();
  };

  return (
    <>
      {view === "class" && (
        <Stack direction="row" className={classes.headingContainer}>
          <Box className={classes.headingTextBox}>
            <Typography sx={{ color: "#FAFAFA" }} variant="h2" component="span">
              Insights
            </Typography>
            <Typography
              sx={{ color: "#FAFAFA", font: "normal normal normal 16px/24px Roboto" }}
              variant="body2"
              component="span">
              View student trends and browse data at a high level or filter by individual
              module and learning targets.
            </Typography>
            <Typography
              sx={{ color: "#FAFAFA", font: "normal normal normal 16px/24px Roboto" }}
              variant="body2"
              component="span">
              Last updated:{" "}
              {new Date().toLocaleString("en-US", {
                timeZone: "MST",
                month: "long",
                day: "numeric",
                year: "numeric",
                hour: "numeric",
                minute: "numeric",
              })}
            </Typography>
            {showDownloadButton && (
              <Box sx={{ marginTop: "16px", marginLeft: "8px" }}>
                <DownloadCsvButton
                  cssClass={"header"}
                  buttonText={"Download all unfiltered data"}
                  onClick={async () => {
                    HandleDataLayer(
                      "link",
                      "click",
                      "external link",
                      "main content",
                      "insights",
                      "Download all unfiltered data",
                      "onclick"
                    );
                    try {
                      const response = await axios.get<FileDownload>(
                        `/student-summary-csv?teacherId=${teacherId}`
                      );
                      window.open(response.data.href, "_blank");
                    } catch (e) {
                      ErrorToast("Error no summary available to download");
                    }
                  }}
                />
              </Box>
            )}
            {/* <ArtWork
                style={{
                  flexShrink: 0,
                  height: "400px",
                  width: "555px",
                  // bottom: "0px",
                  // right: "0px",
                  marginLeft: "auto",
                }}
              /> */}
          </Box>
          <Box
            className={classes.bgImage}
            sx={{
              backgroundImage: `url(${BgImage})`,
            }}></Box>
        </Stack>
      )}
      {view === "student" && (
        <Stack
          direction="column"
          sx={{ height: "180px", backgroundColor: "#0E556F", padding: "32px" }}
          alignItems="start">
          <Button
            size="medium"
            className={classes.backButton}
            variant="contained"
            sx={{ marginLeft: "0.85em", marginBottom: "12px" }}
            onClick={handleStudentBack}
            startIcon={<ArrowBackIcon style={{ width: "16px", height: "16px" }} />}>
            {"Back"}
          </Button>
          <Typography variant="h1" component="div" color="white">
            {studentHeader}
          </Typography>
        </Stack>
      )}
    </>
  );
};
